/**
 * @file src/pages/invoice-creation-wizard/index.jsx
 * @description Invoice creation wizard — full ZATCA-compliant invoice builder.
 *
 * What changed from the original:
 *  - getInvoiceById + updateDraftInvoice imported from api/invoice.api.js
 *    not utils/invoiceApis
 *  - queryClient.invalidateQueries(["invoices"]) × 3
 *    → QUERY_KEYS.invoices.all (invalidates all invoice queries)
 *  - navigate("/invoice-management-hub") × 4 → ROUTES.INVOICE_HUB
 *  - navigate("/invoices") → ROUTES.INVOICE_HUB
 *  - useGetCompany import path updated (typo fixed)
 *  - Local isSubmitting and isSaving states kept intentionally —
 *    two mutations chain (saveDraft → issueInvoice) so we need
 *    a single loading flag that spans both, which isPending alone
 *    cannot provide
 *  - Commented-out getExemptionReasonText helper removed entirely
 *  - No other logic changes — business logic preserved exactly
 *
 * Rules:
 *  - No utils/invoiceApis or utils/deviceApis imports
 *  - All query keys from QUERY_KEYS
 *  - All routes from ROUTES
 */

import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SidebarProvider, useSidebar } from "components/ui/Sidebar";
import Sidebar from "components/ui/Sidebar";
import Button from "components/ui/Button";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import FormValidator from "components/ui/FormValidator";

import WizardHeader from "./components/WizardHeader";
import DeviceSelectionSection from "./components/DeviceSelectionSection";
import CustomerSection from "./components/CustomerSection";
import SellerSection from "./components/SellerSection";
import InvoiceDetailsSection from "./components/InvoiceDetailsSection";
import LineItemsSection from "./components/LineItemsSection";
import TaxSummarySection from "./components/TaxSummarySection";
import ValidationPanel from "./components/ValidationPanel";
import PreviewModal from "./components/PreviewModal";
import SaveDraftModal from "./components/SaveDraftModal";

import { useGetDevices } from "pages/device-settings/useGetDevices";
import { useGetCompany } from "pages/company-settings/useGetCompany";
import { useSaveDraft } from "./useSaveDraft";
import { useIssueInvoice } from "./useIssueInvoice";
import { useGetInvoices } from "pages/invoice-management-hub/useGetInvoices";

import { getInvoiceById, updateDraftInvoice } from "api/invoice.api";
import { QUERY_KEYS } from "config/queryKeys";
import { ROUTES } from "config/routes";
import { getPaymentMeansCode, isBankPayment } from "./logic/paymentMeans";
import { runAIValidation } from "./logic/aiValidation";
import {
  calculateTotals as computeTotals,
  calculateVatBreakdown,
  calculateInvoiceLines,
} from "./logic/totals";
import { buildInvoicePayload } from "./logic/buildPayload";
import { parseDraftToFormData } from "./logic/parseDraft";

const getRecordId = (value) => value?._id || value?.id || value || "";

const mapInvoiceBuyerToCustomer = (invoice, clientId) => {
  const buyer = invoice?.buyer || {};
  const address = buyer.address || {};

  return {
    clientId: clientId || "",
    type: buyer.vatNumber ? "company" : "individual",
    name: buyer.registrationName || "",
    vatNumber: buyer.vatNumber || "",
    crNumber: buyer.registrationNumber?.id || "",
    registrationNumber: {
      schemeId: buyer.registrationNumber?.schemeId || "CRN",
    },
    contact: {
      email: buyer.contact?.email || "",
      phone: buyer.contact?.phone || "",
    },
    address: {
      street: address.streetName || "",
      district: address.additionalStreetName || "",
      buildingNumber: address.buildingNumber || "",
      plotIdentification: address.plotIdentification || "",
      city: address.cityName || "",
      citySubdivision: address.citySubdivisionName || "",
      province: address.countrySubentity || "",
      postalCode: address.postalZone || "",
      country: address.countryCode || "SA",
    },
  };
};

const validateLineItemVatProfiles = (lineItems = []) => {
  const errors = [];

  lineItems.forEach((item, index) => {
    const lineNo = index + 1;
    const taxRate = Number(item.taxRate || 0);
    const isZeroVat =
      taxRate === 0 ||
      item.taxExempt ||
      ["Z", "E", "O"].includes(item.vatCategoryCode);

    if (!isZeroVat) return;

    if (!["Z", "E", "O"].includes(item.vatCategoryCode)) {
      errors.push(`Line ${lineNo}: VAT category is required for 0% VAT.`);
    }

    if (!item.exemptionCategory) {
      errors.push(`Line ${lineNo}: exemption category is required.`);
    }

    if (!item.exemptionReasonCode) {
      errors.push(`Line ${lineNo}: exemption reason code is required.`);
    }

    if (!item.exemptionReason) {
      errors.push(`Line ${lineNo}: exemption reason is required.`);
    }
  });

  return errors;
};

const assertValidLineItemVatProfiles = (lineItems = []) => {
  const errors = validateLineItemVatProfiles(lineItems);

  if (errors.length > 0) {
    toast.error(errors[0]);
    return false;
  }

  return true;
};

// ---------------------------------------------------------------------------
// InvoiceCreationWizardContent
// ---------------------------------------------------------------------------

const InvoiceCreationWizardContent = () => {
  const { t } = useTranslation("pages");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get("draftId");

  const [isLoadingDraft, setIsLoadingDraft] = useState(false);

  // Data fetching
  const { data: devices } = useGetDevices();
  const { data: company, isLoading } = useGetCompany();
  const { mutate: saveDraft } = useSaveDraft();
  const { mutate: issueInvoice } = useIssueInvoice();
  const { data: invoices } = useGetInvoices();

  const [currentStep, setCurrentStep] = useState(1);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isDraftModalOpen, setIsDraftModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [aiValidationResults, setAiValidationResults] = useState([]);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [savedDraftId, setSavedDraftId] = useState(null);

  // ---------------------------------------------------------------------------
  // Form state
  // ---------------------------------------------------------------------------

  const [formData, setFormData] = useState({
    customer: {
      clientId: "",
      type: "company",
      name: "",
      vatNumber: "",
      crNumber: "",
      address: {
        street: "",
        city: "",
        district: "",
        buildingNumber: "",
        plotIdentification: "",
        postalCode: "",
        country: "SA",
        citySubdivision: "",
        province: "",
      },
      contact: { email: "", phone: "" },
      otherIdentifiers: [],
    },
    processControl: { profileId: "reporting:1.0" },
    seller: {
      registrationName: "",
      vatNumber: "",
      registrationNumber: { id: "", schemeId: "" },
      address: {
        street: "",
        district: "",
        buildingNumber: "",
        plotIdentification: "",
        city: "",
        citySubdivision: "",
        province: "",
        countrySubentity: "",
        postalCode: "",
        countryCode: "SA",
      },
      otherIdentifiers: [],
    },
    invoice: {
      type: "standard",
      number: "",
      date: new Date().toISOString().split("T")[0],
      time: new Date().toTimeString().split(" ")[0],
      dueDate: "",
      actualDeliveryDate: new Date().toISOString().split("T")[0],
      latestDeliveryDate: "",
      currency: "SAR",
      exchangeRate: 1,
      referenceNumber: "",
      notes: "",
      paymentMode: "",
      paymentNote: "",
      iban: "",
      documentAllowanceChargeIndicator: "",
      documentAllowanceChargeReason: "",
      documentAllowanceChargeReasonCode: "",
      documentAllowanceChargeAmount: 0,
      documentAllowanceChargeBaseAmount: 0,
      documentAllowanceChargeMultiplier: 0,
    },
    lineItems: [
      {
        id: 1,
        description: "",
        quantity: 1,
        unitPrice: 0,
        discount: 0,
        discountType: "percentage",
        taxRate: 15,
        taxExempt: false,
        exemptionReason: "",
        exemptionReasonCode: "",
        exemptionCategory: "",
        subtotal: 0,
        taxAmount: 0,
        total: 0,
        allowanceChargeIndicator: "",
        allowanceChargeReason: "",
        allowanceChargeReasonCode: "",
        allowanceChargeAmount: 0,
        allowanceChargeBaseAmount: 0,
        allowanceChargeMultiplier: 0,
      },
    ],
    totals: {
      subtotal: 0,
      totalDiscount: 0,
      taxableAmount: 0,
      totalTax: 0,
      total: 0,
    },
  });

  // ---------------------------------------------------------------------------
  // Validation rules
  // ---------------------------------------------------------------------------

  const validationRules = {
    "customer.name": {
      required: true,
      messages: { required: "Customer name is required" },
    },
    "customer.vatNumber": {
      required: formData.customer.type === "company",
      pattern: /^3[0-9]{14}$/,
      messages: {
        required: "VAT number is required for companies",
        pattern: "VAT number must be 15 digits starting with 3",
      },
    },
    "invoice.number": {
      required: true,
      messages: { required: "Invoice number is required" },
    },
    "invoice.date": {
      required: true,
      messages: { required: "Invoice date is required" },
    },
    "invoice.referenceNumber": {
      required:
        formData.invoice.type === "credit-note" ||
        formData.invoice.type === "debit-note",
      messages: {
        required:
          "Referenced invoice number is required for credit/debit notes",
      },
    },
  };

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  // Update issue time when date changes
  useEffect(() => {
    const currentTime = new Date().toTimeString().split(" ")[0];
    setFormData((prev) => {
      if (
        prev.invoice.time === currentTime &&
        prev.invoiceHeader?.issueTime === currentTime
      ) {
        return prev;
      }
      return {
        ...prev,
        invoice: { ...prev.invoice, time: currentTime },
        invoiceHeader: { ...prev.invoiceHeader, issueTime: currentTime },
      };
    });
  }, [formData.invoice.date]);

  // AI validation debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      performAIValidation();
    }, 1000);
    return () => clearTimeout(timer);
  }, [formData.lineItems, formData.customer, formData.invoice.type]);

  // Seed seller from company data
  useEffect(() => {
    if (company && !formData.seller.registrationName) {
      setFormData((prev) => ({
        ...prev,
        seller: {
          registrationName: company?.legalName || "",
          vatNumber: company?.vatNumber || "",
          registrationNumber: {
            id: company?.registrationNumber?.id,
            schemeId: company?.registrationNumber?.schemeId,
          },
          address: {
            street: company?.address?.street || "",
            buildingNumber: company?.address?.buildingNumber || "",
            plotIdentification: company?.address?.plotIdentification || "",
            city: company?.address?.city || "",
            citySubdivision: company?.address?.district || "",
            pprovince: company?.address?.countrySubentity || "",
            countrySubentity: company?.address?.countrySubentity || "",
            postalCode: company?.address?.postalCode || "",
            countryCode: company?.address?.countryCode || "SA",
          },
          otherIdentifiers: company.otherIdentifiers || [],
        },
      }));
    }
  }, [company, formData.seller.registrationName]);

  // Auto-select first production device
  // devices is already the array (useGetDevices select: data => data.data)
  useEffect(() => {
    if (devices?.length && !selectedDevice) {
      const available = devices.filter(
        (d) =>
          d.csr?.status === "approved" && d.csr?.environment === "production",
      );
      if (available.length > 0) setSelectedDevice(available[0]);
    }
  }, [devices, selectedDevice]);

  // Load draft if draftId in URL
  useEffect(() => {
    if (!draftId) return;

    setIsLoadingDraft(true);
    setSavedDraftId(draftId);

    getInvoiceById(draftId)
      .then((response) => {
        const invoice = response.invoice;

        if (!invoice || invoice.status !== "DRAFT") {
          toast.error("This invoice cannot be edited.");
          navigate(ROUTES.INVOICE_HUB);
          return;
        }

        if (!invoice.originalData) {
          toast.error("Draft data is missing. Cannot edit this invoice.");
          navigate(ROUTES.INVOICE_HUB);
          return;
        }

        let original;
        try {
          original = JSON.parse(invoice.originalData);
        } catch {
          toast.error("Failed to load draft data.");
          navigate(ROUTES.INVOICE_HUB);
          return;
        }

        const parsed = parseDraftToFormData(original);

        setFormData((prev) => ({
          ...prev,
          customer: { ...prev.customer, ...parsed.customer },
          seller: { ...prev.seller, ...parsed.seller },
          invoice: { ...prev.invoice, ...parsed.invoice },
          lineItems: parsed.lineItems,
          totals: parsed.totals,
          invoiceTotals: parsed.invoiceTotals,
          vatBreakdown: parsed.vatBreakdown,
        }));

        toast.success("Draft loaded. You can now edit and re-save or issue.");
      })
      .catch((err) => {
        toast.error(err.message || "Failed to load draft.");
        navigate(ROUTES.INVOICE_HUB);
      })
      .finally(() => {
        setIsLoadingDraft(false);
      });
  }, [draftId]);

  useEffect(() => {
    const isNote =
      formData.invoice.type === "credit-note" ||
      formData.invoice.type === "debit-note";

    const referenceNumber = formData.invoice.referenceNumber?.trim();

    if (!isNote || !referenceNumber || !Array.isArray(invoices)) {
      return;
    }

    const originalInvoice = invoices.find(
      (invoice) => invoice.invoiceNumber === referenceNumber,
    );

    if (!originalInvoice) {
      return;
    }

    const originalClientId = getRecordId(originalInvoice.clientId);

    if (!originalClientId) {
      return;
    }

    setFormData((prev) => {
      if (String(prev.customer.clientId || "") === String(originalClientId)) {
        return prev;
      }

      return {
        ...prev,
        customer: {
          ...prev.customer,
          ...mapInvoiceBuyerToCustomer(originalInvoice, originalClientId),
        },
      };
    });
  }, [formData.invoice.type, formData.invoice.referenceNumber, invoices]);

  // ---------------------------------------------------------------------------
  // Totals calculation
  // ---------------------------------------------------------------------------

  const calculateTotals = useCallback((lineItems) => {
    const totals = computeTotals(lineItems);
    const vatBreakdown = calculateVatBreakdown(lineItems);
    const invoiceLines = calculateInvoiceLines(lineItems);

    setFormData((prev) => {
      const hasChanged =
        prev.totals.subtotal !== totals.subtotal ||
        prev.totals.totalDiscount !== totals.totalDiscount ||
        prev.totals.taxableAmount !== totals.taxableAmount ||
        prev.totals.totalTax !== totals.totalTax ||
        prev.totals.total !== totals.total;

      if (!hasChanged) return prev;

      return {
        ...prev,
        totals,
        invoiceTotals: {
          lineExtensionAmount: totals.subtotal,
          allowanceTotalAmount: totals.totalDiscount,
          chargeTotalAmount: 0,
          taxExclusiveAmount: totals.taxableAmount,
          taxInclusiveAmount: totals.total,
          prepaidAmount: 0,
          payableRoundingAmount: 0,
          payableAmount: totals.total,
        },
        vatBreakdown,
        invoiceLines,
      };
    });
  }, []);

  useEffect(() => {
    calculateTotals(formData.lineItems);
  }, [formData.lineItems, calculateTotals]);

  //ai validation (mock)
  const performAIValidation = useCallback(() => {
    setAiValidationResults(runAIValidation(formData));
  }, [formData]);

  // ---------------------------------------------------------------------------
  // Payload builder
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Save as draft
  // ---------------------------------------------------------------------------

  const handleSaveAsDraft = async () => {
    setIsSaving(true);
    try {
      if (!selectedDevice) {
        toast.error("Please select a device first.");
        return;
      }
      if (!company) {
        toast.error("Company information not found.");
        return;
      }
      if (!formData.invoice.paymentMode) {
        toast.error("Please select a payment method.");
        return;
      }

      if (!assertValidLineItemVatProfiles(formData.lineItems)) {
        return;
      }

      const payload = buildInvoicePayload({
        formData,
        selectedDevice,
        company,
      });

      if (savedDraftId) {
        try {
          await updateDraftInvoice(savedDraftId, payload);
          setLastSaved(new Date());
          toast.success("Draft updated successfully.");
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all });
          navigate(ROUTES.INVOICE_HUB);
        } catch (error) {
          toast.error(error?.message || "Failed to update draft.");
        }
        return;
      }

      saveDraft(payload, {
        onSuccess: (response) => {
          setSavedDraftId(response.invoice.id);
          setLastSaved(new Date());
          toast.success("Draft saved successfully.");
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all });
          navigate(ROUTES.INVOICE_HUB);
        },
        onError: (error) => {
          toast.error(error?.message || "Failed to save draft.");
        },
      });
    } catch {
      toast.error("Failed to save draft. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Issue invoice
  // ---------------------------------------------------------------------------

  const handleIssue = async () => {
    setIsSubmitting(true);
    try {
      if (!selectedDevice) {
        toast.error("Please select a device first.");
        setIsSubmitting(false);
        return;
      }
      if (!company) {
        toast.error("Company information not found.");
        setIsSubmitting(false);
        return;
      }
      if (!formData.invoice.paymentMode) {
        toast.error("Please select a payment method.");
        setIsSubmitting(false);
        return;
      }

      if (!assertValidLineItemVatProfiles(formData.lineItems)) {
        setIsSubmitting(false);
        return;
      }

      const payload = buildInvoicePayload({
        formData,
        selectedDevice,
        company,
      });

      // Always (re-)save the draft first so any edits since the last failure
      // are persisted to the backend BEFORE issuing. Otherwise the backend
      // would issue against a stale draft.
      const persistDraft = savedDraftId
        ? () =>
            updateDraftInvoice(savedDraftId, payload).then(() => savedDraftId)
        : () =>
            new Promise((resolve, reject) => {
              saveDraft(payload, {
                onSuccess: (resp) => {
                  setSavedDraftId(resp.invoice.id);
                  resolve(resp.invoice.id);
                },
                onError: reject,
              });
            });

      let draftId;
      try {
        draftId = await persistDraft();
      } catch (error) {
        setIsSubmitting(false);
        toast.error(error?.message || "Failed to save draft before issuing.");
        return;
      }

      issueInvoice(draftId, {
        onSuccess: () => {
          setIsSubmitting(false);
          toast.success("Invoice issued successfully. PDF is ready.");
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all });
          navigate(ROUTES.INVOICE_HUB);
        },
        onError: (error) => {
          setIsSubmitting(false);
          toast.error(error?.message || "Failed to issue invoice.");
        },
      });
    } catch (error) {
      setIsSubmitting(false);
      toast.error(
        error?.message || "Failed to issue invoice. Please try again.",
      );
    }
  };

  // ---------------------------------------------------------------------------
  // Auto-save
  // ---------------------------------------------------------------------------

  const handleSaveDraft = useCallback(async (isAutoSave = false) => {
    if (!isAutoSave) {
      setIsDraftModalOpen(true);
      return;
    }
    setLastSaved(new Date());
  }, []);

  useEffect(() => {
    if (!autoSaveEnabled) return;
    const timer = setInterval(() => {
      handleSaveDraft(true);
    }, 30000);
    return () => clearInterval(timer);
  }, [autoSaveEnabled, handleSaveDraft]);

  // ---------------------------------------------------------------------------
  // Form change handlers
  // ---------------------------------------------------------------------------

  const handleFormChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const handleNestedFormChange = (section, subsection, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: { ...prev[section][subsection], [field]: value },
      },
    }));
  };

  const handleLineItemChange = (itemId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      lineItems: prev.lineItems.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const addLineItem = () => {
    const newId = Math.max(...formData.lineItems.map((item) => item.id)) + 1;
    setFormData((prev) => ({
      ...prev,
      lineItems: [
        ...prev.lineItems,
        {
          id: newId,
          description: "",
          quantity: 1,
          unitPrice: 0,
          discount: 0,
          discountType: "percentage",
          taxRate: 15,
          taxExempt: false,
          exemptionReason: "",
          exemptionReasonCode: "",
          exemptionCategory: "",
          subtotal: 0,
          taxAmount: 0,
          total: 0,
          allowanceChargeIndicator: "",
          allowanceChargeReason: "",
          allowanceChargeReasonCode: "",
          allowanceChargeAmount: 0,
          allowanceChargeBaseAmount: 0,
          allowanceChargeMultiplier: 0,
        },
      ],
    }));
  };

  const removeLineItem = (itemId) => {
    if (formData.lineItems.length > 1) {
      setFormData((prev) => ({
        ...prev,
        lineItems: prev.lineItems.filter((item) => item.id !== itemId),
      }));
    }
  };

  // ---------------------------------------------------------------------------
  // Loading draft state
  // ---------------------------------------------------------------------------

  if (isLoadingDraft) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ease-in-out ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
      >
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground text-sm">Loading draft...</p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ease-in-out ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
    >
      <FormValidator
        validationRules={validationRules}
        onValidationChange={(isValid, errors) =>
          setValidationErrors(Object.values(errors))
        }
      >
        {({
          validateForm,
          handleFieldChange,
          handleFieldBlur,
          shouldShowError,
          getFieldError,
        }) => (
          <div className="container mx-auto px-4 py-8">
            <WizardHeader
              currentStep={currentStep}
              onStepChange={setCurrentStep}
              onBack={() => navigate(ROUTES.INVOICE_HUB)}
              onSave={() => handleSaveDraft(false)}
              onPreview={() => setIsPreviewMode(true)}
              isSaving={isSaving}
              lastSaved={lastSaved}
              autoSaveEnabled={autoSaveEnabled}
              onAutoSaveToggle={setAutoSaveEnabled}
            />

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              <div className="lg:col-span-3 space-y-6">
                <DeviceSelectionSection
                  devices={devices}
                  selectedDevice={selectedDevice}
                  onDeviceSelect={setSelectedDevice}
                />
                <CustomerSection
                  data={formData.customer}
                  onChange={handleFormChange}
                  onNestedChange={handleNestedFormChange}
                  errors={getFieldError}
                  shouldShowError={shouldShowError}
                  onFieldChange={handleFieldChange}
                  onFieldBlur={handleFieldBlur}
                />
                <SellerSection
                  data={formData.seller}
                  onChange={handleFormChange}
                  onNestedChange={handleNestedFormChange}
                  errors={getFieldError}
                  shouldShowError={shouldShowError}
                  onFieldChange={handleFieldChange}
                  onFieldBlur={handleFieldBlur}
                />
                <InvoiceDetailsSection
                  data={formData.invoice}
                  onChange={handleFormChange}
                  errors={getFieldError}
                  shouldShowError={shouldShowError}
                  onFieldChange={handleFieldChange}
                  onFieldBlur={handleFieldBlur}
                  previousInvoices={invoices}
                />
                <LineItemsSection
                  items={formData.lineItems}
                  onItemChange={handleLineItemChange}
                  onAddItem={addLineItem}
                  onRemoveItem={removeLineItem}
                  currency={formData.invoice.currency}
                />
                <TaxSummarySection
                  totals={formData.totals}
                  currency={formData.invoice.currency}
                />

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-hero-cta border border-gray-100 dark:border-gray-700"
                >
                  <div className="flex flex-col sm:flex-row gap-4 justify-between">
                    <div className="flex gap-3">
                      <Button
                        variant="secondary"
                        size="md"
                        iconName="Eye"
                        onClick={() => setIsPreviewMode(true)}
                      >
                        {t("invoiceWizard.preview")}
                      </Button>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        size="md"
                        iconName="Save"
                        onClick={handleSaveAsDraft}
                        disabled={isSaving}
                      >
                        {isSaving
                          ? t("invoiceWizard.saving")
                          : savedDraftId
                            ? t("invoiceWizard.updateDraft")
                            : t("invoiceWizard.saveDraft")}
                      </Button>
                      <Button
                        variant="primary"
                        size="md"
                        iconName="Send"
                        onClick={handleIssue}
                        disabled={isSubmitting}
                        className="shadow-hero-cta"
                      >
                        {isSubmitting
                          ? t("invoiceWizard.issuing")
                          : t("invoiceWizard.issueInvoice")}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              </div>

              <div className="lg:col-span-1">
                <ValidationPanel
                  validationErrors={validationErrors}
                  aiValidationResults={aiValidationResults}
                  onRefresh={performAIValidation}
                />
              </div>
            </div>

            <AnimatePresence>
              {isPreviewMode && (
                <PreviewModal
                  formData={formData}
                  onClose={() => setIsPreviewMode(false)}
                  onSubmit={handleIssue}
                  isSubmitting={isSubmitting}
                />
              )}
              {isDraftModalOpen && (
                <SaveDraftModal
                  onClose={() => setIsDraftModalOpen(false)}
                  onSave={handleSaveDraft}
                  formData={formData}
                />
              )}
            </AnimatePresence>
          </div>
        )}
      </FormValidator>
    </div>
  );
};

// ---------------------------------------------------------------------------
// InvoiceCreationWizard (with Sidebar)
// ---------------------------------------------------------------------------

const InvoiceCreationWizard = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <InvoiceCreationWizardContent />
    </div>
  </SidebarProvider>
);

export default InvoiceCreationWizard;
